import {
  BuildingEnergyModelFragment,
  EnergySystemType,
  HourSeriesType,
} from '../../../generated/graphql'
import {
  averageByEvenNumberRange,
  averageByHour,
  dayHours,
  hoursOfTheDay,
  monthsSumSerie,
  rangeValues,
  tempCutSerie,
  yearMonths,
} from './calibration-util'
import {
  calcSpaceHeatingDensityHourSeries,
  spaceHeatingCalculator,
} from '../../../energy-system/space-heating-hour-series-calculator'
import { hoursInYear } from '../../../util/hours-in-year'
import { InputHourSerieType } from '../../../building-energy-model/hour-series-calculator'
import { calcHeatLoadHourSeries } from '../../../energy-system/heatload-hour-series-calculator'
import { i18n } from '../../../i18n'
import {
  createChartSerie,
  createChartSerieValues,
  createDefaultLineChartSerie,
  CustomLineStyles,
  CustomSeries,
} from './chart-util'

const DEFAULT_SPACEHEATING_LINETYPE = CustomLineStyles.Solid
const DEFAULT_SPACEHEATING_LINEWIDTH = 0.1

export const calcSpaceHeatingByHourChartData = (
  model: BuildingEnergyModelFragment,
  outsideTempHourSerie: number[],
  cutSerie: number[],
  ventilationHeatingDemandSerie: number[],
  year: number,
): CustomSeries => {
  const id = i18n.t('buildingEnergyModels.calibration.dataTypes.spaceHeating')

  const hourValues = dayHours()

  const result = createDefaultLineChartSerie(
    id,
    hourValues,
    DEFAULT_SPACEHEATING_LINETYPE,
    DEFAULT_SPACEHEATING_LINEWIDTH,
    true,
  )

  const spaceHeating = model.energySystems.find(
    (s) => s.type === EnergySystemType.SpaceHeating,
  )

  if (!spaceHeating) return result

  const { area } = model

  const heatloadResult = calcHeatLoadHourSeries(
    model,
    outsideTempHourSerie,
    area,
    year,
  )

  const heatLoadSupplySerie =
    heatloadResult.inputs.find(
      (r) => r.inputSerieType === InputHourSerieType.HeatLoadHeatloadSupply,
    )?.hourSerie ?? Array<number>(hoursInYear).fill(0)

  const spaceHeatingHourSerieResult = spaceHeatingCalculator(
    outsideTempHourSerie,
    ventilationHeatingDemandSerie,
    heatLoadSupplySerie,
    spaceHeating,
    year,
  )

  const spaceHeatingSerie = spaceHeatingHourSerieResult.outputs
    .find((r) => r.hourSerieType === HourSeriesType.HeatingDemand)
    ?.hourSerie.map((i) => i / area)

  if (!spaceHeatingSerie) return result

  const hours = hoursOfTheDay(0)

  const spaceHeatingData = averageByHour(
    hourValues,
    spaceHeatingSerie,
    cutSerie,
    hours,
  )

  result.data = createChartSerieValues(hourValues, spaceHeatingData)

  return result
}

export const calcSpaceHeatingInZeroTempByHourChartData = (
  model: BuildingEnergyModelFragment,
  outsideTempHourSerie: number[],
  calScheduleSerie: number[],
  ventilationHeatingDemandSerie: number[],
  year: number,
): CustomSeries => {
  const cutSerie = tempCutSerie(
    model.calOutdoorHourSeries?.values ?? [],
    calScheduleSerie,
    model.calibrationLimitValues?.zeroWeatherTempStart ?? 100,
    model.calibrationLimitValues?.zeroWeatherTempEnd ?? -100,
  )

  return calcSpaceHeatingByHourChartData(
    model,
    outsideTempHourSerie,
    cutSerie,
    ventilationHeatingDemandSerie,
    year,
  )
}

export const calcSpaceHeatingByEvenTempRangeChartData = (
  model: BuildingEnergyModelFragment,
  tempRangeStart: number,
  tempRangeEnd: number,
  tempRangeStep: number,
  outsideTempHourSerie: number[],
  cutSerie: number[],
  ventilationHeatingDemandSerie: number[],
  year: number,
): CustomSeries => {
  const id = i18n.t('buildingEnergyModels.calibration.dataTypes.spaceHeating')

  const temperatureValues = rangeValues(
    tempRangeStart,
    tempRangeEnd,
    tempRangeStep,
  )

  const result = createDefaultLineChartSerie(
    id,
    temperatureValues,
    DEFAULT_SPACEHEATING_LINETYPE,
    DEFAULT_SPACEHEATING_LINEWIDTH,
    true,
  )

  const spaceHeating = model.energySystems.find(
    (s) => s.type === EnergySystemType.SpaceHeating,
  )

  if (!spaceHeating) return result

  const { area } = model

  const heatloadResult = calcHeatLoadHourSeries(
    model,
    outsideTempHourSerie,
    area,
    year,
  )

  const heatLoadSupplySerie =
    heatloadResult.inputs.find(
      (r) => r.inputSerieType === InputHourSerieType.HeatLoadHeatloadSupply,
    )?.hourSerie ?? (Array.from(Array(hoursInYear).fill(0)) as number[])

  const spaceHeatingHourSerieResult = spaceHeatingCalculator(
    outsideTempHourSerie,
    ventilationHeatingDemandSerie,
    heatLoadSupplySerie,
    spaceHeating,
    year,
  )

  const spaceHeatingSerie = spaceHeatingHourSerieResult.outputs
    .find((r) => r.hourSerieType === HourSeriesType.HeatingDemand)
    ?.hourSerie.map((i) => i / area)

  if (!spaceHeatingSerie) return result

  const spaceHeatingData = averageByEvenNumberRange(
    temperatureValues,
    outsideTempHourSerie,
    spaceHeatingSerie,
    cutSerie,
  )

  result.data = createChartSerieValues(temperatureValues, spaceHeatingData)

  return result
}

export const calcSumSpaceHeatingByMonthChartData = (
  model: BuildingEnergyModelFragment,
  outsideTempHourSerie: number[],
  ventilationHeatingDemandSerie: number[],
  area: number,
  year: number,
): CustomSeries => {
  const id = i18n.t('buildingEnergyModels.calibration.dataTypes.spaceHeating')

  const spaceHeatingDensitySerie = calcSpaceHeatingDensityHourSeries(
    model,
    outsideTempHourSerie,
    ventilationHeatingDemandSerie,
    area,
    year,
  )

  const monthValues = yearMonths()

  const spaceHeatingData = monthsSumSerie(spaceHeatingDensitySerie, year)

  return createChartSerie(
    id,
    monthValues,
    spaceHeatingData,
    DEFAULT_SPACEHEATING_LINETYPE,
    DEFAULT_SPACEHEATING_LINEWIDTH,
    true,
  )
}
